/**
 * Done → next. Finishing a task (tick on Today, or Done on the task page) shows a small bar with what to do next:
 * the next open task in today's plan, else the best task for today. Open it, skip to another, or dismiss.
 */
import { Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, SkipForward, X } from "lucide-react";
import { useEffect, useState, useSyncExternalStore } from "react";
import { api } from "../lib/api";

interface Next { id: string; ref: string; title: string; priority: string; reason: string; nextAction: string | null; start: string | null; fromPlan: boolean }

let state: { after: string | null; title: string | null; n: number } = { after: null, title: null, n: 0 };
const listeners = new Set<() => void>();
/** Call after a task was finished. */
export function showNext(afterId: string, finishedTitle?: string) {
  state = { after: afterId, title: finishedTitle ?? null, n: state.n + 1 };
  listeners.forEach((l) => l());
}
const useDone = () => useSyncExternalStore((l) => (listeners.add(l), () => listeners.delete(l)), () => state);

const hm = (iso: string) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Malta" });

export function DoneNextBar() {
  const s = useDone();
  const [open, setOpen] = useState(false);
  const [skip, setSkip] = useState<string[]>([]);
  const [data, setData] = useState<{ next: Next | null; planLeft: number | null } | null>(null);

  useEffect(() => {
    if (!s.after) return;
    setOpen(true);
    setSkip([]);
    setData(null);
  }, [s.n, s.after]);

  useEffect(() => {
    if (!open || !s.after) return;
    const qs = new URLSearchParams({ after: s.after, ...(skip.length ? { skip: skip.join(",") } : {}) });
    let live = true;
    api<{ next: Next | null; planLeft: number | null }>(`/api/plan/next?${qs}`).then((r) => live && setData(r)).catch(() => live && setData({ next: null, planLeft: null }));
    return () => { live = false; };
  }, [open, s.after, skip]);

  if (!open || !data) return null;
  const n = data.next;
  return (
    <div className="fixed inset-x-3 bottom-20 z-40 mx-auto max-w-xl rounded-2xl border border-[var(--line)] bg-[var(--panel)] p-3 shadow-[var(--shadow-pop)] md:bottom-6" role="status">
      <div className="flex items-start gap-3">
        <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-emerald-600" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Done{s.title ? ` — ${s.title}` : ""}</p>
          {n ? (
            <>
              <p className="muted mt-1 text-xs">{n.fromPlan ? `Next in your plan${n.start ? ` · ${hm(n.start)}` : ""}` : `Next up · ${n.reason}`}{data.planLeft !== null ? ` · ${data.planLeft} left today` : ""}</p>
              <p className="mt-0.5 truncate text-sm font-medium">{n.title}</p>
              {n.nextAction && <p className="muted truncate text-xs">Next: {n.nextAction}</p>}
            </>
          ) : <p className="muted mt-1 text-sm">{data.planLeft === 0 ? "That was the last one in your plan. Nice." : "Nothing else urgent for today."}</p>}
        </div>
        <button className="btn btn-ghost btn-sm !px-1.5" onClick={() => setOpen(false)} aria-label="Close"><X size={15} /></button>
      </div>
      {n && (
        <div className="mt-2 flex justify-end gap-2">
          <button className="btn btn-sm" onClick={() => setSkip((k) => [...k, n.id])}><SkipForward size={14} /> Something else</button>
          <Link to="/work/$id" params={{ id: n.id }} className="btn btn-sm btn-primary" onClick={() => setOpen(false)}>Open it <ArrowRight size={14} /></Link>
        </div>
      )}
    </div>
  );
}
